<template>
  <div class="guided-search-content-form__location">
    <base-step :title="trans('guided_search.location.title')" main-class="budget-step-container">
      <template #content>
        <div v-if="!isSecondStep" class="general-tags main-location__tags">
          <TypeFormButton
            v-for="(tag, index) in getLocationOptions"
            :key="index"
            :content="tag.name"
            :active="tag.active"
            @clicked="selectLocationChoice(tag)"
          />
        </div>
        <div v-if="isSecondStep && locationChoice && locationChoice.tags" class="general-tags location__tags">
          <TypeFormButton
            v-for="(tag, index) in getTags"
            :key="index"
            :content="tag.content"
            :active="tag.active"
            @clicked="selectTag(tag)"
          />
        </div>
        <div v-if="isSecondStep && locationChoice && locationChoice.id === 0" class="location__other">
          <span class="location__field">
            <input
              v-model="locationTownInput.value"
              type="text"
              :class="{ error: locationTownInput.touched && isLocationTownInputInvalid }"
              :placeholder="trans('guided_search.location.town')"
              @input="sendDataStore"
              @blur="locationTownInput.touched = true"
            >
            <label v-if="locationTownInput.touched && isLocationTownInputInvalid">
              {{ trans('global.form.field.invalid', { field: trans('guided_search.location.town') }) }}
            </label>
          </span>
          <span class="location__field">
            <input
              v-model="locationPostalCodeInput.value"
              type="text"
              :class="{ 'error': locationPostalCodeInput.touched && isPostalCodeInvalid }"
              :placeholder="trans('guided_search.location.postal_code')"
              @input="sendDataStore"
              @blur="locationPostalCodeInput.touched = true"
            >
            <label v-if="locationPostalCodeInput.touched && isPostalCodeInvalid">
              {{ trans('global.form.field.invalid', { field: trans('guided_search.location.postal_code') }) }}
            </label>
          </span>
        </div>
      </template>
    </base-step>
  </div>
</template>

<script>
import eventBus from '@tools/event-bus';
import TypeFormButton from '@components-molecules/TypeFormButton.vue';
import guidedSearchMixin from '../guidedSearchMixin';
import { cities, defaultPostalCodeCities } from '../guidedSearchEnum';
import BaseStep from './BaseStep.vue';

export default {
  name: 'Location',
  components: { TypeFormButton, BaseStep },
  mixins: [ guidedSearchMixin ],
  data() {
    return {
      showSelectList: true,
      otherOption: {
        id: 0,
        name: this.trans('guided_search.location.select.other'),
      },
      locationTownInput: {
        value: '',
        touched: false,
      },
      locationPostalCodeInput: {
        value: '',
        touched: false,
      },
      locationChoice: null,
      parisDistrict: [],
      isSecondStep: false,
    };
  },
  computed: {
    getTags() {
      if (!this.locationChoice) {
        return [];
      }
      return this[this.locationChoice.tags];
    },
    getParisDistrict() {
      const districts = [];
      window.initialAppState.paris_district.forEach((value) => {
        districts.push({ content: value, active: false });
      });
      return districts;
    },
    getLocationOptions() {
      const options = [];
      cities.forEach((town, i) => {
        options.push({
          id: i + 1,
          name: town,
          tags: i === 0 ? 'parisDistrict' : null,
          active: i + 1 === this.locationChoice?.id,
        });
      });
      options.push(this.otherOption);
      options[options.length - 1].active = this.locationChoice?.id === this.otherOption.id;
      return options;
    },
  },
  created() {
    this.initSteps();
    this.sendDataStore();
  },
  mounted() {
    window.dataLayer.push({
      event: 'screenChange',
      event_name: 'localisation',
      step: 'localisation',
      funnel: 'support-contact-form',
    });

    this.resetLocationListener = () => {
      this.isSecondStep = false;
      this.initSteps(true);
    };

    eventBus.addEventListener('reset-component-location', this.resetLocationListener);
  },
  beforeUnmount() {
    eventBus.removeEventListener('reset-component-location', this.resetLocationListener);
  },
  methods: {
    initSteps(isFromThisStep = false) {
      this.parisDistrict = this.getParisDistrict;
      const dataLocation = this.$store.getters['formGuidedSearch/getStepValue']('location');

      // Retrieve data from store when it exists
      if (dataLocation) {
        this.locationChoice = dataLocation.locationChoice;
        this.locationTownInput.value = dataLocation.locationTownInput;
        this.locationPostalCodeInput.value = dataLocation.locationPostalCodeInput;

        this.isSecondStep = false;
        if (!isFromThisStep && dataLocation.isSecondStep) {
          this.isSecondStep = true;
        }

        if (this.locationChoice?.tags && dataLocation.selectedTagsCity) {
          dataLocation.selectedTagsCity.forEach((v) => {
            const index = this[this.locationChoice.tags].findIndex((t) => t.content === v);
            this[this.locationChoice.tags][index].active = true;
          });
        }
      }
    },
    formatCity() {
      this.locationTownInput.value = this.locationTownInput.value
        ? this.locationTownInput.value.replace(/[0-9]/g, '')
        : null;
    },
    limitZipCode() {
      if (this.locationPostalCodeInput.value?.length > 5) {
        this.locationPostalCodeInput.value = this.locationPostalCodeInput.value.slice(0, 5);
      }
    },
    sendDataStore() {
      if (!this.locationChoice) {
        return;
      }
      this.formatCity();
      this.limitZipCode();
      this.$store.commit('formGuidedSearch/SET_STEP_VALUE', {
        step: 'location',
        value: {
          locationTownInput: this.locationTownInput.value,
          locationPostalCodeInput: this.locationPostalCodeInput.value,
          locationChoice: this.locationChoice,
          defaultLocationPostalCode:
            this.locationChoice && this.locationChoice.id === 0
              ? null
              : defaultPostalCodeCities[this.getLocationOptions.findIndex((v) => v.name === this.locationChoice.name)],
          selectedTagsCity: this.locationChoice?.tags
            ? this[this.locationChoice.tags].filter((v) => v.active).map((v) => v.content)
            : null,
          isSecondStep: this.isSecondStep,
        },
      });
    },
    selectTag(tag) {
      const index = this[this.locationChoice.tags].findIndex((t) => t.content === tag.content);
      this[this.locationChoice.tags][index].active = !tag.active;
      this.sendDataStore();
    },
    async selectLocationChoice(tag) {
      const previousValue = this.$store.getters['formGuidedSearch/getStepValue']('location');
      if (previousValue?.locationChoice?.id === tag?.id) {
        this.locationChoice = null;
        this.$store.commit('formGuidedSearch/SET_STEP_VALUE', {
          step: 'location',
          value: {
            locationTownInput: null,
            locationPostalCodeInput: null,
            locationChoice: null,
            defaultLocationPostalCode: null,
            selectedTagsCity: null,
            isSecondStep: false,
          },
        });
        return;
      }

      this.locationChoice = tag;
      this.$emit('set-loader', true);
      await this.waitTime(400);
      this.$emit('set-loader', false);
      this.showSelectList = false;
      this.sendDataStore();
      if (this.locationChoice?.id > 1) {
        this.$emit('next-step');
      }
      this.isSecondStep = true;
      if (tag) {
        window.dataLayer.push({
          event: 'screenChange',
          event_name: 'localisation_ville',
          step: 'localisation',
          value: tag.name,
        });
      }
    },
  },
};
</script>
